/**
 * Identity helpers for finding sessions by email/phone/customer-id.
 *
 * The dashboard never sends raw email/phone to the server — values are hashed
 * client-side to match how Foober's identifyCustomer() stores them on the
 * session's user_traits JSON. Customer IDs are sent verbatim (they're already
 * opaque UUIDs).
 */

export type IdentityKind = 'emailHash' | 'phoneHash' | 'customerId' | 'invalid';

export interface ResolvedIdentity {
  kind: IdentityKind;
  /** value to send to the API — either a sha256 hex or the raw customer id */
  value: string;
  /** human-readable label of what we interpreted the input as */
  label: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizePhone(phone: string): string {
  // Strip everything but digits, then drop leading 0 or +61 to match identifyCustomer's normalization
  const digits = phone.replace(/[^\d]/g, '');
  if (digits.startsWith('61') && digits.length >= 11) return digits.slice(2);
  if (digits.startsWith('0')) return digits.slice(1);
  return digits;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Take an arbitrary string the user typed into the identity search box and
 * return what kind of identifier it is + the value to send to the API.
 *
 * Resolution rules (in priority order):
 *   1. Matches email pattern → emailHash (sha256 of normalized email)
 *   2. Matches UUID pattern → customerId (passed verbatim)
 *   3. Has at least 6 digits → phoneHash (sha256 of normalized phone)
 *   4. Otherwise → invalid
 */
export async function resolveIdentityQuery(raw: string): Promise<ResolvedIdentity> {
  const trimmed = raw.trim();
  if (!trimmed) return { kind: 'invalid', value: '', label: '' };

  if (EMAIL_RE.test(trimmed)) {
    const hash = await sha256Hex(normalizeEmail(trimmed));
    return { kind: 'emailHash', value: hash, label: 'email' };
  }

  if (UUID_RE.test(trimmed)) {
    return { kind: 'customerId', value: trimmed, label: 'customer ID' };
  }

  const digitsOnly = trimmed.replace(/[^\d]/g, '');
  if (digitsOnly.length >= 6) {
    const hash = await sha256Hex(normalizePhone(trimmed));
    return { kind: 'phoneHash', value: hash, label: 'phone' };
  }

  return { kind: 'invalid', value: '', label: '' };
}

/**
 * Pull the display-ready identity bits out of a session's user_traits JSON.
 * Defensive against undefined / wrong-shape traits.
 */
export interface SessionIdentity {
  displayName: string | null;
  emailMasked: string | null;
  phoneMasked: string | null;
  customerId: string | null;
  isSubscriber: boolean;
}

export function readSessionIdentity(
  userId: string | null | undefined,
  userTraits: Record<string, unknown> | null | undefined,
): SessionIdentity | null {
  if (!userId && !userTraits) return null;
  const t = (userTraits ?? {}) as Record<string, unknown>;
  const displayName = typeof t.displayName === 'string' ? t.displayName : null;
  const emailMasked = typeof t.emailMasked === 'string' ? t.emailMasked : null;
  const phoneMasked = typeof t.phoneMasked === 'string' ? t.phoneMasked : null;
  const customerId =
    typeof t.customerId === 'string' ? t.customerId : userId || null;
  const isSubscriber = !!t.isSubscriber;

  if (!displayName && !emailMasked && !phoneMasked && !customerId) return null;
  return { displayName, emailMasked, phoneMasked, customerId, isSubscriber };
}
